import * as React from 'react';

export interface ErrorBoundaryState {
    hasError: boolean;
    error: Error | null;
}

export interface ErrorBoundaryProps {
    children: React.ReactNode;
    fallback?: React.ComponentType<ErrorBoundaryFallbackProps>;
}

export interface ErrorBoundaryFallbackProps {
    error: Error | null;
    retry: () => void;
}

function DefaultErrorFallback({error, retry}: ErrorBoundaryFallbackProps) {
    return (
        <div className="min-h-screen bg-gradient-to-br from-slate-50 to-slate-100 flex items-center justify-center p-4">
            <div className="w-full max-w-md">
                <div className="bg-white rounded-xl shadow-lg overflow-hidden">
                    <div className="bg-red-600 px-6 py-4">
                        <h1 className="text-white text-xl font-semibold text-center">
                            エラーが発生しました
                        </h1>
                    </div>
                    <div className="p-6">
                        <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-md">
                            <p className="text-sm text-red-700">
                                {error?.message || '予期しないエラーが発生しました。'}
                            </p>
                        </div>
                        <div className="text-center">
                            <button
                                onClick={retry}
                                className="px-4 py-2 bg-red-600 text-white rounded-md hover:bg-red-700 transition-colors"
                            >
                                再試行
                            </button>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
}

export class ErrorBoundary extends React.Component<ErrorBoundaryProps, ErrorBoundaryState> {
    constructor(props: ErrorBoundaryProps) {
        super(props);
        this.state = {hasError: false, error: null};
    }

    static getDerivedStateFromError(error: Error): ErrorBoundaryState {
        // エラーが発生した時の状態更新
        return {hasError: true, error};
    }

    componentDidCatch(error: Error, errorInfo: React.ErrorInfo) {
        // エラーログの記録
        console.error('ErrorBoundary caught an error:', error, errorInfo);
    }

    handleRetry = () => {
        // エラー状態をリセット
        this.setState({hasError: false, error: null});
    };

    render() {
        if (this.state.hasError) {
            const FallbackComponent = this.props.fallback || DefaultErrorFallback;
            return (
                <FallbackComponent
                    error={this.state.error}
                    retry={this.handleRetry}
                />
            );
        }

        return this.props.children;
    }
}
